import {
  useState,
  useCallback,
  useRef,
  useMemo,
  Fragment,
  useEffect,
} from "react";
import { useTheme, IconButton, Drawer, Button } from "@mui/material";
import LiveHelpIcon from "@mui/icons-material/LiveHelp";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import SendIcon from "@mui/icons-material/Send";
import cn from "classnames";
import { motion, AnimatePresence } from "framer-motion";
import Markdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { isEqual } from "lodash";
import { differenceInSeconds } from "date-fns";

import styles from "./styles.module.css";

import { wait } from "./utils";

import {
  Message,
  OpenThreadInput,
  OpenThreadResponse,
  PostMessageToThreadInput,
  GetMessageInput,
} from "./types";

import WebOnly from "../WebOnly";
import Logo from "../Logo";
import { useTrueBizApi, TrueBizApi } from "../../api";

const openThread = async (
  api: TrueBizApi,
  { subjectId, subjectType }: OpenThreadInput
): Promise<OpenThreadResponse> => {
  console.debug("Opening new chat thread");
  const thread = await api.startAssistantChatThread({ subjectId, subjectType });
  console.debug(`Opened new chat thread with id ${thread.thread_id}`);
  return thread;
};

const postMessageToThread = async (
  api: TrueBizApi,
  { threadId, content }: PostMessageToThreadInput
): Promise<Message> => {
  console.debug(`Posting new chat message to thread ${threadId}`);
  return api.postAssistantChatThreadMessage({ content, threadId });
};

const getMessage = async (
  api: TrueBizApi,
  { threadId, messageId }: GetMessageInput
): Promise<Message> => {
  console.debug(`Getting chat message ${messageId} in thread ${threadId}`);
  return api.getAssistantChatThreadMessage({ threadId, messageId });
};

interface WaitForMessageToFinishInput {
  threadId: UUID;
  messageId: UUID;
  timeout?: number;
  pollingIntervalMs?: number;
  pollingIntervalJitterMs?: number;
}

const waitForMessageToFinish = async function* (
  api: TrueBizApi,
  {
    threadId,
    messageId,
    timeout = 180_000,
    pollingIntervalMs = 750,
    pollingIntervalJitterMs = 250,
  }: WaitForMessageToFinishInput
) {
  console.debug(
    `Waiting ${timeout} ms for message ${messageId} in thread ${threadId} to finish. ` +
      `Polling every ${pollingIntervalMs} ms with ${pollingIntervalJitterMs} ms jitter.`
  );

  const timeoutPromise = new Promise<never>((resolve, reject) => {
    setTimeout(() => {
      reject(`Waiting for message ${messageId} timed out after ${timeout} ms`);
    }, timeout);
  });

  let lastMessage: Message | undefined = undefined;

  const startedAt = performance.now();

  while (true) {
    const currentMessage = await Promise.race([
      getMessage(api, { threadId, messageId }),
      timeoutPromise,
    ]);

    if (["finished", "error"].includes(currentMessage.status)) {
      console.group("Message finished");
      console.debug(
        `Message has finished with terminal state "${currentMessage.status}" ` +
          `after ${((performance.now() - startedAt) / 1_000).toFixed(
            2
          )} seconds.`
      );
      console.debug(
        `Final message`,
        JSON.stringify(currentMessage, null, "  ")
      );
      console.groupEnd();

      yield currentMessage;
      return;
    }

    if (!isEqual(lastMessage, currentMessage)) {
      console.group("Message updated");
      console.debug(`Message has been updated since last poll.`);
      console.debug(
        `Current message`,
        JSON.stringify(currentMessage, null, "  ")
      );
      console.debug(
        `Previous message`,
        JSON.stringify(lastMessage, null, "  ")
      );
      console.groupEnd();

      lastMessage = currentMessage;
      yield currentMessage;
    }

    await wait(pollingIntervalMs + pollingIntervalJitterMs * Math.random());
  }
};

const extractAnswerText = (jsonString: string) => {
  try {
    const parsed = JSON.parse(jsonString);
    const answer = parsed?.answer;
    if (answer && typeof answer === "string") {
      return answer;
    }
  } catch (e) {
    console.error(e);
  }

  return "";
};

const extractAnswerSources = (jsonString: string) => {
  const _hasStringProp = (o: any, propName: string) => {
    if (o == null) return false;
    if (!(propName in o)) return false;
    if (typeof o[propName] !== "string") return false;

    return true;
  };

  const _isFullURL = (s: string) => {
    // some citations are not full urls, but relative file paths
    try {
      const url = new URL(s);
      return ["http:", "https:"].includes(url.protocol);
    } catch {
      return false;
    }
  };

  try {
    const parsed = JSON.parse(jsonString);
    const sources = parsed?.sources;
    if (sources && Array.isArray(sources)) {
      return sources.filter((source) => {
        return (
          _hasStringProp(source, "source_type") &&
          _hasStringProp(source, "url") &&
          _isFullURL(source.url) &&
          _hasStringProp(source, "name")
        );
      });
    }
  } catch (e) {
    console.error(e);
  }
  return [];
};

export interface Props {
  reportId: UUID;
}

export default function Investigator({ reportId }: Props) {
  const api = useTrueBizApi();

  const questionRef = useRef<HTMLTextAreaElement | null>(null);
  const messageListRef = useRef<HTMLDivElement | null>(null);

  const theme = useTheme();
  const [isActive, setIsActive] = useState(false);
  const [threadId, setThreadId] = useState<Promise<UUID> | null>(null);

  // as far as I can tell there's no good way to manage this using mui props
  // directly. we need to allow the document to scroll, since the drawer background
  // slaps a style="overflow: hidden" on the body element. this is cursed, but
  // so is mui kinda
  useEffect(() => {
    if (!isActive) return;
    const observer = new MutationObserver((mutations) => {
      if (mutations.length > 0) {
        document.body.style.overflow = "";
      }
    });

    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ["style"],
    });

    return () => {
      observer.disconnect();
    };
  }, [isActive]);

  // worse comes to worse can use a ref with { [message_id]: time_elapsed }
  // for keeping track of the thinking timings for each message if the
  // created_at and updated_at times are wonky

  const exampleQuestions = useMemo(
    () => [
      // "Are there signs this business might be fraudulent?",
      // "Could this be a front for transaction laundering?",
      // "Do product prices match the type of goods offered?",
      // "Is their web traffic consistent with other activity signals?",
    ],
    []
  );

  const openInvestigator = useCallback(() => {
    console.debug(`Opening the investigator`);

    // open the drawer
    setIsActive(true);

    // don't block waiting for the thread to open
    setThreadId(
      new Promise((resolve) => {
        console.debug(`Requesting new chat thread`);

        openThread(api, {
          subjectId: reportId,
          subjectType: "WPR",
        }).then((thread) => {
          resolve(thread.thread_id);
        });
      })
    );
  }, [reportId, api]);

  const closeInvestigator = useCallback(() => {
    console.debug(`Closing the investigator`);

    // close the drawer
    setIsActive(false);

    // clear the local state
    setMessageBuffer("");
    setMessages([]);
    setThreadId(null);
  }, []);

  const [messages, setMessages] = useState<Message[]>([]);
  const [messageBuffer, setMessageBuffer] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitUserMessage = useCallback(
    async (message?: string) => {
      const content = message || messageBuffer;
      if (!content) return;
      if (!threadId) return; // this should never happen, since we create the promise on drawer open

      console.debug(`Submitting user question:`, content);

      setIsSubmitting(true);

      try {
        if (message) {
          setMessageBuffer("");
        }

        const userMessageCreatedAt = new Date().toISOString();
        const userMessage: Message = {
          created_at: userMessageCreatedAt,
          updated_at: userMessageCreatedAt,
          id: crypto.randomUUID(),
          role: "user",
          status: "finished",
          author: "user",
          content: [
            {
              type: "text",
              text: {
                value: content,
                annotations: [],
              },
            },
          ],
        };

        const botMessage = await postMessageToThread(api, {
          threadId: await threadId,
          content,
        });

        setMessageBuffer("");

        setMessages((prevMessages) => [
          ...prevMessages,
          userMessage,
          botMessage,
        ]);

        setTimeout(() => {
          messageListRef.current?.scrollTo({
            top: messageListRef.current?.scrollHeight,
            behavior: "smooth",
          });
        }, 0);

        for await (const updatedMessage of waitForMessageToFinish(api, {
          threadId: await threadId,
          messageId: botMessage.id,
        })) {
          setMessages((prevMessages) => {
            const updatedMessages: Message[] = [];

            for (const prevMessage of prevMessages) {
              if (prevMessage.id !== updatedMessage.id) {
                updatedMessages.push(prevMessage);
                continue;
              }

              updatedMessages.push(updatedMessage);
            }

            return updatedMessages;
          });

          setTimeout(() => {
            messageListRef.current?.scrollTo({
              top: messageListRef.current?.scrollHeight,
              behavior: "smooth",
            });
          }, 0);
        }

        console.debug(`User question submission was successful`);
      } catch (e) {
        console.error(`User question submission failed`, e);
      } finally {
        setIsSubmitting(false);
        setTimeout(() => {
          questionRef.current?.focus();
        }, 0);
      }
    },
    [messageBuffer, threadId, api]
  );

  return (
    <WebOnly>
      {!isActive && (
        <Button
          size="large"
          style={{
            position: "fixed",
            bottom: "0.75em",
            right: "0.75em",
            border: "1px solid",
            background: "white",
            borderColor: "#ddd",
            zIndex: "999999",
            textTransform: "none",
          }}
          sx={{ boxShadow: 1 }}
          startIcon={<LiveHelpIcon />}
          onClick={() => openInvestigator()}
        >
          <span>
            <strong>Uncover more</strong> - Ask the AI Agent
          </span>
        </Button>
      )}
      <Drawer
        open={isActive}
        anchor="right"
        slotProps={{
          backdrop: { invisible: true },

          paper: {
            sx: {
              backgroundColor: "white",
            },
          },
        }}
        onClose={() => closeInvestigator()}
        keepMounted={false}
      >
        <div
          style={{
            position: "relative",
            minWidth: "350px",
            maxWidth: "500px",
            width: "50vw",
            height: "100%",
            maxHeight: "100%",
            overflow: "hidden",
          }}
        >
          <IconButton
            style={{
              position: "absolute",
              top: "0.5em",
              left: "0.5em",
            }}
            onClick={() => closeInvestigator()}
            title="Close Investigator"
          >
            <CloseOutlinedIcon />
          </IconButton>
          <div
            style={{
              paddingTop: "3.5em",
              height: "100%",
              width: "100%",
            }}
          >
            <div
              style={{
                textAlign: "center",
                display: "flex",
                justifyContent: "center",
                alignItems: messages.length > 0 ? "flex-start" : "center",
                height: "100%",
              }}
            >
              <AnimatePresence>
                <motion.div
                  style={{
                    width: "100%",
                    height:
                      messages.length > 0 ? "calc(100% - 3.5em)" : undefined,
                    position: "relative",
                  }}
                >
                  {messages.length === 0 && (
                    <>
                      <motion.h3>
                        What do you want to know about this business?
                      </motion.h3>
                    </>
                  )}
                  <motion.div
                    layout
                    key="messages-list"
                    className="messages-list"
                    ref={messageListRef}
                    style={{
                      overflow: "scroll",
                      width: "100%",
                      height: messages.length > 0 ? "100%" : 0,
                      padding: messages.length === 0 ? 0 : "0.5em",
                    }}
                  >
                    {messages.map((message) => {
                      return (
                        <motion.div
                          key={message.id}
                          className={cn(styles.message, {
                            [styles.user]: message.role === "user",
                            [styles.bot]: message.role === "assistant",
                          })}
                        >
                          {message.status === "finished" && (
                            <motion.div
                              className={styles.content}
                              key={`${message.id}-finished-content`}
                            >
                              {message.role === "assistant" && (
                                <div
                                  style={{
                                    display: "flex",
                                    flexDirection: "row",
                                    gap: "0.5em",
                                    alignItems: "center",
                                    marginBottom: "0.75em",
                                  }}
                                >
                                  <div
                                    style={{
                                      background: theme.palette.primary.main,
                                      width: "36px",
                                      height: "36px",
                                      borderRadius: "15%",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      paddingLeft: "0.25em",
                                    }}
                                  >
                                    <Logo small reversed />
                                  </div>
                                  <div
                                    style={{
                                      fontSize: "90%",
                                      color: theme.palette.grey[600],
                                    }}
                                  >
                                    Thought for{" "}
                                    {differenceInSeconds(
                                      message.updated_at,
                                      message.created_at
                                    )}{" "}
                                    seconds
                                  </div>
                                </div>
                              )}
                              {message.role === "user" ? (
                                message.content
                                  .filter((c) => c.type === "text")
                                  .map((c) => c.text.value)
                                  .join(" ")
                              ) : (
                                <div>
                                  <Markdown remarkPlugins={[remarkGfm]}>
                                    {message.content
                                      .filter((c) => c.type === "text")
                                      .map((c) =>
                                        extractAnswerText(c.text.value)
                                      )
                                      .filter((c) => !!c)
                                      .join(" ") ||
                                      "Sorry, there was a problem. Please try your question again"}
                                  </Markdown>
                                  {message.content
                                    .filter((c) => c.type === "text")
                                    .flatMap((c) =>
                                      extractAnswerSources(c.text.value)
                                    )
                                    .map((source, i) => (
                                      <Fragment key={`${source.url}__${i}`}>
                                        {i === 0 && (
                                          <div
                                            style={{
                                              color: theme.palette.grey[600],
                                            }}
                                          >
                                            Sources
                                          </div>
                                        )}
                                        <div
                                          style={{
                                            lineHeight: 1.25,
                                          }}
                                        >
                                          <a
                                            href={source.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            style={{
                                              color: theme.palette.grey[600],
                                              fontSize: "88%",
                                            }}
                                          >
                                            {source.name}
                                          </a>
                                        </div>
                                      </Fragment>
                                    ))}
                                </div>
                              )}
                            </motion.div>
                          )}
                          {message.status === "processing" && (
                            <>
                              <motion.div
                                animate={{ rotate: [0, 360] }}
                                transition={{
                                  duration: 3,
                                  repeat: Infinity,
                                  repeatType: "mirror",
                                  repeatDelay: 0.25,
                                  type: "spring",
                                  stiffness: 80,
                                }}
                                style={{
                                  marginRight: "0.5em",
                                  marginTop: "-0.3em",
                                }}
                              >
                                <div
                                  style={{
                                    background: theme.palette.primary.main,
                                    width: "36px",
                                    height: "36px",
                                    borderRadius: "15%",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    paddingLeft: "0.25em",
                                  }}
                                >
                                  <Logo small reversed />
                                </div>
                              </motion.div>
                              <div className={styles.processing}>
                                {message.content
                                  .filter((c) => c.type === "text")
                                  .map((c) => c.text.value)
                                  .join(" ")}
                              </div>
                            </>
                          )}
                          {message.status === "error" && (
                            <div className={styles.error}>
                              <strong>There was an error.</strong>
                              <br />
                              Please ask your question again.
                            </div>
                          )}
                        </motion.div>
                      );
                    })}
                  </motion.div>
                  <motion.div
                    style={{
                      display: "flex",
                      justifyContent: "center",
                      position: messages.length > 0 ? "sticky" : undefined,
                      bottom: messages.length > 0 ? "0.75em" : undefined,
                      zIndex: messages.length > 0 ? 1 : undefined,
                      width:
                        messages.length > 0
                          ? "calc(100% - 1.5em)"
                          : "calc(100% - 3em)",
                      border: "1px solid #cfcfcf",
                      background: "white",
                      borderRadius: "0.75em",
                      gap: "0.5em",
                      padding: "0.5em",
                      paddingRight: 0,
                      marginLeft: messages.length > 0 ? "0.75em" : "1.5em",
                      marginRight: messages.length > 0 ? "0.75em" : "1.5em",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flex: 1,
                        alignItems: "center",
                      }}
                    >
                      <textarea
                        value={messageBuffer}
                        onChange={(e) => {
                          setMessageBuffer(e.target.value);
                        }}
                        onKeyDown={(e) => {
                          // the default behavior of text areas is (reasonably)
                          // to allow enter to create a new line. We only want to allow shift enter
                          // and otherwise trigger submission if the user hits enter
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.stopPropagation();
                            e.preventDefault();
                            submitUserMessage();
                          }
                        }}
                        ref={questionRef}
                        className={cn(styles.question)}
                        placeholder={
                          messages.length > 0
                            ? "Ask anything"
                            : "Start your investigation…"
                        }
                        style={{ flex: 1 }}
                        disabled={isSubmitting}
                      />
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "flex-end",
                      }}
                    >
                      <IconButton
                        disabled={!messageBuffer.length || isSubmitting}
                        onClick={() => {
                          submitUserMessage();
                        }}
                      >
                        <SendIcon fontSize="small" />
                      </IconButton>
                    </div>
                  </motion.div>
                  {messages.length === 0 && (
                    <motion.div
                      key="options-buttons"
                      style={{
                        display: "grid",
                        gridTemplateColumns: "1fr",
                        rowGap: "0.25em",
                        columnGap: "0.25em",
                        paddingLeft: "1.5em",
                        paddingRight: "1.5em",
                        paddingTop: "0.5em",
                        marginTop: "1em",
                        paddingBottom: "1em",
                      }}
                      exit={{
                        opacity: 0,
                        height: 0,
                        overflow: "hidden",
                      }}
                    >
                      {exampleQuestions.map((question, i) => (
                        <Button
                          key={i}
                          onClick={() => {
                            submitUserMessage(question);
                          }}
                          disabled={isSubmitting}
                          variant="outlined"
                          sx={{
                            textTransform: "none",
                            color: theme.palette.grey[800],
                            backgroundColor: theme.palette.grey[50],
                            borderColor: theme.palette.grey[300],
                          }}
                          color="primary"
                          size="small"
                        >
                          {question}
                        </Button>
                      ))}
                    </motion.div>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </div>
      </Drawer>
    </WebOnly>
  );
}
